import styled from '@emotion/styled';
import { AppBar as _AppBar, Button, Typography } from '@material-ui/core';
import _LaunchIcon from '@material-ui/icons/Launch';
import { createElement, FC, MouseEventHandler } from 'react';
import { FormattedMessage } from 'react-intl';

import Language from '../../../../entity/Language';
import { OrganizationDetailFragment } from '../../../../generated/graphql';
import { sidebarBackground } from '../../../constant/color';
import { SPACING_REGULAR, SPACING_UNIT } from '../../../constant/spacing';
import SideBarIframeInternalLinkMenuButton from './SideBarIframeInternalLinkMenuButton';

type Props = {
  selectedOrganization: OrganizationDetailFragment | null;
  onClickHomeButton: MouseEventHandler<HTMLElement>;
  onClickProjectsButton: MouseEventHandler<HTMLElement>;
  onClickNewProjectButton: MouseEventHandler<HTMLElement>;
  onClickCreative: MouseEventHandler<HTMLElement>;
  onClickSettings: MouseEventHandler<HTMLElement>;
  language: Language;
  className?: string;
};

const SideBarIframe: FC<Props> = ({
  selectedOrganization,
  onClickHomeButton,
  onClickProjectsButton,
  onClickNewProjectButton,
  onClickCreative,
  onClickSettings,
  className,
  language,
}) => {
  return (
    <SideBar color="default" elevation={2} className={className} data-testid="SideBar">
      <SideBarIframeInternalLinkMenuButton
        onClick={onClickHomeButton}
        path={'/latest-insights'}
        dataIntercomTarget={'sidebar-home-button'}
        messageId={'sidebar.home'}
      />

      <SideBarIframeInternalLinkMenuButton
        onClick={onClickNewProjectButton}
        path={'/direction_types'}
        dataIntercomTarget={'sidebar-new-project-button'}
        messageId={'sidebar.new_project'}
      />

      <SideBarIframeInternalLinkMenuButton
        onClick={onClickProjectsButton}
        path={'/projects'}
        dataIntercomTarget={'sidebar-project-list-button'}
        messageId={'sidebar.project_list'}
      />

      {(selectedOrganization?.businessRegion.key === 'jp' || (!selectedOrganization && language === 'ja-JP')) && (
        <SideBarIframeInternalLinkMenuButton
          onClick={onClickCreative}
          path={'/search'}
          dataIntercomTarget={'sidebar-creative-search-button'}
          messageId={'sidebar.creative'}
        />
      )}

      <SideBarIframeInternalLinkMenuButton
        onClick={onClickSettings}
        path={'/settings'}
        dataIntercomTarget={'sidebar-project-list-button'}
        messageId={'sidebar.settings'}
      />

      <Link
        href="https://help.kaizen-ad.com/"
        target="_blank"
        rel="noopener noreferrer"
        data-intercom-target={'sidebar-project-list-button'}
      >
        <MenuButton>
          <Label>
            <FormattedMessage id="sidebar.help" />
          </Label>
          <LaunchIcon />
        </MenuButton>
      </Link>
    </SideBar>
  );
};

const SideBar = styled(_AppBar)({
  width: '240px',
  height: '100%',
  paddingLeft: SPACING_REGULAR,
  paddingRight: SPACING_REGULAR,
  paddingTop: SPACING_UNIT * 3,
  right: 'auto',
  left: '0',
  backgroundColor: '#fff',
  border: 'none',
  boxShadow: '0px 8px 10px -5px #0003',
  overflowY: 'auto',
});

const MenuButton = styled(Button)({
  lineHeight: '1.1875rem',
  textDecoration: 'none',
  display: 'flex',
  alignItems: 'center',
  width: '208px',
  height: '40px',
  borderRadius: '8px',
  cursor: 'pointer',
  justifyContent: 'unset',
  backgroundColor: 'transparent',
  '&:hover': {
    background: sidebarBackground,
    boxShadow: 'unset',
  },
});

const Link = styled('a')({
  textDecoration: 'none',
});

const LaunchIcon = styled(_LaunchIcon)({
  color: '#4c566b',
  position: 'absolute',
  top: '9px',
  right: '10px',
  height: '20px',
});

const Label = styled(Typography)({
  color: '#4c566b',
  fontWeight: 700,
  fontSize: '14px',
  margin: '10px 16px',
  whiteSpace: 'nowrap',
});
Label.defaultProps = { variant: 'caption' };

export default SideBarIframe;
